import { useRef, useEffect } from 'react';
import { useAnalytics } from '../contexts/AnalyticsContext';

/** Maximum tracked duration per card (5 minutes) */
const MAX_DURATION_MS = 5 * 60 * 1000;

/**
 * Hook to track visibility duration of an event card using IntersectionObserver.
 * - Threshold: 0.8 (80% of the card must be visible)
 * - Accumulates time across entry/exit cycles (scroll up/down)
 * - Pauses timer when tab/app goes to background (visibilitychange)
 * - Caps duration at MAX_DURATION_MS to filter outliers
 * - Sends a single `apparition_evenement` with `duration_ms` on unmount
 * - Uses `pagehide` (more reliable on mobile than `beforeunload`)
 *
 * @param scrollContainerRef - Optional root for IntersectionObserver (e.g. drawer scroll container).
 *   When provided, visibility is relative to the container, not the viewport.
 */
export function useVisibilityTracker(
  elementRef: React.RefObject<HTMLElement | null>,
  event: { id: string; [key: string]: any },
  viewSource: 'card' | 'carte_preview',
  scrollContainerRef?: React.RefObject<HTMLElement | null>
) {
  const analytics = useAnalytics();
  const entryTimeRef = useRef<number | null>(null);
  const totalMsRef = useRef(0);
  const sentRef = useRef(false);
  const eventRef = useRef(event);

  // Keep event ref fresh (avoids stale closure on unmount)
  eventRef.current = event;

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return;

    // Reset on mount
    sentRef.current = false;
    totalMsRef.current = 0;
    entryTimeRef.current = null;

    // --- Helpers ---

    /** Pause the running timer and accumulate elapsed time */
    const pauseTimer = () => {
      if (entryTimeRef.current !== null) {
        totalMsRef.current += Date.now() - entryTimeRef.current;
        entryTimeRef.current = null;
      }
    };

    /** Resume the timer (only if observer had marked element as intersecting) */
    // We track whether the element is currently intersecting so we can
    // correctly resume after a visibilitychange.
    let isIntersecting = false;

    const resumeTimer = () => {
      if (isIntersecting && entryTimeRef.current === null) {
        entryTimeRef.current = Date.now();
      }
    };

    // --- IntersectionObserver ---

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            isIntersecting = true;
            // Card entered visible area — start timer (only if page is visible)
            if (entryTimeRef.current === null && document.visibilityState === 'visible') {
              entryTimeRef.current = Date.now();
            }
          } else {
            isIntersecting = false;
            // Card left visible area — accumulate duration
            pauseTimer();
          }
        }
      },
      {
        threshold: 0.8,
        root: scrollContainerRef?.current ?? null,
      }
    );

    observer.observe(el);

    // --- visibilitychange: pause when tab/app goes to background ---

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        pauseTimer();
      } else {
        resumeTimer();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // --- Send accumulated visibility event ---

    const sendEvent = () => {
      if (sentRef.current) return;
      pauseTimer();
      // Cap duration to filter extreme outliers
      const cappedMs = Math.min(totalMsRef.current, MAX_DURATION_MS);
      if (cappedMs > 0) {
        sentRef.current = true;
        analytics.trackEventAppearance(eventRef.current, viewSource, cappedMs);
      }
    };

    // pagehide is more reliable than beforeunload on mobile browsers
    window.addEventListener('pagehide', sendEvent);

    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pagehide', sendEvent);
      sendEvent(); // Send on component unmount
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event.id, viewSource]);
}
